import React, { PropsWithChildren, useState } from 'react';
import { MdAdd } from 'react-icons/md';

import { AddButton } from 'ui';

import { isDisabled } from 'app/infrastructure/data/date';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { ComplexValue, LoadOptions, RequestRemoteApi } from 'app/types-shared';
import * as navigate from 'app/area/shared/navigation';
import { DragListContainer } from 'app/area/shared/list-components/drag-list-container';
import { PopupAddNew } from 'app/area/shared/list-components/popup-add-new';

import { getNextElement, getPrevElement } from '../utils/get-saved-report';
import { SavedReportsTranslation, SavedReportsEndpoints, SavedReportCategory } from '../constants';
import { GenericSavedReport, SavedReportsValidation, SavedReportValidation } from '../types-store';
import {
  AddSavedReportsHandler, OnUpdateSavedReport, SavedReportFormRenderer,
  SavedReportInfoRenderer, UpdateSavedReportHandler, ValidateSavedReportHandler
} from '../types';

import { renderSavedReportDragPreview } from './components/saved-report-drag-preview';
import { SavedReportDragPreviewItemProps } from './components/saved-report-drag-preview-item';
import { SavedReportTile } from './components/saved-report-tile';
import { SavedReportDragItemType } from './constants';
import { SavedReportTileConfig } from './types';

interface SavedReportsProps<SavedReport extends GenericSavedReport> {
  savedReports: SavedReport[];
  currentSavedReportId: number;
  currentSavedReport: SavedReport;
  currentSavedReportOnlyIdMode: boolean;
  currentValidation: SavedReportValidation<SavedReport>;
  displayDisabled: boolean;

  renderSavedReportInfo?: SavedReportInfoRenderer<SavedReport>;
  renderSavedReportForm: SavedReportFormRenderer<SavedReport>;

  createDefaultSavedReport: (code: ComplexValue<number>, sequence: number) => SavedReport;
  addSavedReports: AddSavedReportsHandler<SavedReport>;
  updateSavedReport: UpdateSavedReportHandler<SavedReport>;
  deleteSavedReport: (savedReportId: number) => void;

  deleteAllSavedReports: () => void;
  fetchingAddSavedReports: boolean;

  validateSavedReport: ValidateSavedReportHandler<SavedReport>;

  selectCurrent: (savedReport: SavedReport, onlyIdMode: boolean) => void;
  updateValidation: (validation: SavedReportValidation<SavedReport>) => void;

  loadEvaluation: (accessType: number, favouriteId: number, onUpdateSavedReport: OnUpdateSavedReport<SavedReport>) => void;
  displayError: (errorMessage: string) => void;
  switchDisplayDisabled: (displayDisabled: boolean) => void;

  endpoints: SavedReportsEndpoints;
  savedReportCategories: SavedReportCategory | SavedReportCategory[];

  accessType?: number;

  options: {
    [key: string]: {
      fetching: boolean;
      codes?: ComplexValue<number | string>[];
    };
  };
  loadOptions: (id: string,
    componentEndpoint: string,
    loadOptionsParams: LoadOptions,
    optionsParams?: any) => void;

  savedReportsValidation: SavedReportsValidation<SavedReport>;
  translation: SavedReportsTranslation;
  locale: string;
  header?: React.ReactNode;
  tileConfig: SavedReportTileConfig;
}

const mySavedReportsId = 'mySavedReportsId';

const getKey = (id: number, sequence: number) => isDefinedIncludingZero(id)
  ? `id_${id}` : `sequence_${sequence}`;

// eslint:disable-next-line: max-func-body-length
export const SavedReports = <SavedReport extends GenericSavedReport>({
  savedReports, currentSavedReportId, currentSavedReport, deleteAllSavedReports, currentValidation,
  renderSavedReportInfo, renderSavedReportForm,
  createDefaultSavedReport, addSavedReports, updateSavedReport, deleteSavedReport,
  fetchingAddSavedReports, displayDisabled, tileConfig,
  validateSavedReport, selectCurrent, updateValidation,
  loadEvaluation, displayError,
  endpoints, savedReportCategories, options, loadOptions,
  accessType, savedReportsValidation, translation, locale, header
}: PropsWithChildren<SavedReportsProps<SavedReport>>) => {

  const allSavedReportIs = savedReports?.map(report => report.code?.value);

  const onAddById = (sequence?: number) => {
    const emptySavedReport = createDefaultSavedReport(undefined, sequence ?? savedReports.length + 1);
    updateSavedReport(undefined, emptySavedReport, undefined);
    selectCurrent(emptySavedReport, true);
  };

  const addByIdHandler = () => {
    onAddById();
  };

  const loadSavedReportOptions = (loadOptionsParams: LoadOptions) => {
    loadOptions(mySavedReportsId, endpoints.savedReportsOptions, {
      ...loadOptionsParams,
      requestRemoteApi: RequestRemoteApi.servicesApi
    },
      {
        ...(isDefinedIncludingZero(accessType) && { accessType: accessType }),
        reportCategory: savedReportCategories
      });
  };

  const [isOpen, setIsOpen] = useState(false);
  const [addReportsSequence, setAddReportsSequence] = useState<number>();

  const onOpenAddSavedReports = (sequence?: number) => {
    setAddReportsSequence(sequence);
    setIsOpen(true);
  };

  const openAddSavedReportsHandler = () => {
    onOpenAddSavedReports();
  };

  const closeAddSavedReportsHandler = () => {
    setIsOpen(false);
  };

  const onAddSavedReports = (values: ComplexValue<number>[]) => {
    const newSavedReports = values
      .filter(value => !allSavedReportIs.includes(value.value))
      .map((value, index) => createDefaultSavedReport(value, savedReports.length + index + 1));

    addSavedReports(newSavedReports, addReportsSequence ?? savedReports.length + 1, accessType);
  };

  const onManageSavedReports = () => {
    navigate.toSearchView(true);
  };

  const footer = <>
    <AddButton icon={MdAdd} onClick={openAddSavedReportsHandler} disabled={fetchingAddSavedReports}>
      {translation.add_reports}
    </AddButton>
    <AddButton icon={MdAdd} onClick={addByIdHandler}>
      {translation.add_report_by_id}
    </AddButton>
  </>;

  const filteredReports = savedReports?.filter(savedReport => displayDisabled || !isDisabled(savedReport?.disabled));

  return <>
    <SavedReportDragPreviewItemProps
      renderItem={renderSavedReportDragPreview(translation)}
      itemType={SavedReportDragItemType.savedReportTile} />
    <DragListContainer header={header} footer={footer}>
      {filteredReports
        ?.map(
          (savedReport, index) => {
            const prevElement = getPrevElement(filteredReports, index, displayDisabled);
            const nextElement = getNextElement(filteredReports, index, displayDisabled);

            return <SavedReportTile
              key={getKey(savedReport.code?.value, savedReport.sequence)}
              savedReport={savedReport}
              accessType={accessType}
              currentSavedReportId={currentSavedReportId}
              currentSavedReport={currentSavedReportId === savedReport.code?.value ? currentSavedReport : undefined}
              currentValidation={currentSavedReportId === savedReport.code?.value ? currentValidation : undefined}
              deleteAllSavedReports={deleteAllSavedReports}
              allSavedReportIs={allSavedReportIs}
              prevElementSequence={prevElement?.sequence}
              nextElementSequence={nextElement?.sequence}
              renderSavedReportInfo={renderSavedReportInfo}
              renderSavedReportForm={renderSavedReportForm}
              onAddReports={onOpenAddSavedReports}
              onAddById={onAddById}
              validateSavedReport={validateSavedReport}
              updateSavedReport={updateSavedReport}
              deleteSavedReport={deleteSavedReport}
              selectCurrent={selectCurrent}
              updateValidation={updateValidation}
              loadEvaluation={loadEvaluation}
              displayError={displayError}
              validation={savedReportsValidation}
              translation={translation}
              tileConfig={tileConfig} />;
          }
        )}
    </DragListContainer>
    {isOpen && <PopupAddNew
      title={translation.add_saved_reports_title}
      addItems={onAddSavedReports}
      onClose={closeAddSavedReportsHandler}
      actionTitle={translation.add_new_report}
      onAction={onManageSavedReports}
      options={options?.[mySavedReportsId]?.codes as ComplexValue<number>[]}
      fetchingOptions={options?.[mySavedReportsId]?.fetching || false}
      loadOptions={loadSavedReportOptions}
      descriptionAllLevels={false}
      doneLabel={translation.done}
      searchPlaceholder={translation.search}
      locale={locale}
    />}
  </>;
};
